import { Navigate, Outlet, useLocation } from 'react-router';
import { UniversalLoadingState } from '@afrilink/ui';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { isStaffUser, isSupportUser } from './defaultRouteForUser';

/**
 * Route guard for the admin console (`/admin/*`).
 *
 * - Anonymous users → /sign-in.
 * - Sales-side staff (sales_agent / sales_manager) → bounced to /sales
 *   (their console — SALES-ISO-1 / LD-S11 explicit isolation).
 * - Customers (no staff role claim) → bounced to /app.
 * - Staff (`staff`, `admin`, `super_admin`) → admitted.
 *
 * The internal SightHost role is read from `auth.users.raw_app_meta_data.role`,
 * which the Supabase JS client surfaces as `user.app_metadata.role`. External
 * sales staff (Phase 11) do NOT carry that field — they're recognised by
 * `staff_profile.role` via useStaffRole.
 */
export function StaffRoute() {
  const { loading, isAuthenticated, user } = useAuth();
  const { loading: roleLoading, isSalesStaff } = useStaffRole();
  const location = useLocation();

  if (loading || roleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-card-subtle">
        <UniversalLoadingState variant="spinner" caption="Loading…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // Support agents (2026-07-06 re-scope): admitted to the OPERATIONAL admin
  // surface (customers, hosting, domains, per-client billing, support), but
  // blocked from SightHost-confidential areas — the aggregate financial Reports
  // section, Settings, and the financial dashboard at /admin. Those redirect to
  // /admin/customers. The data layer (is_full_staff views/tables) enforces the
  // same boundary regardless of the URL.
  if (!isStaffUser(user) && isSupportUser(user)) {
    const path = location.pathname;
    const blocked =
      path === '/admin' ||
      path === '/admin/' ||
      path.startsWith('/admin/reports') ||
      path.startsWith('/admin/settings');
    if (blocked) {
      return <Navigate to="/admin/customers" replace />;
    }
    return <Outlet />;
  }

  // SALES-ISO-1: external sales staff get bounced to their own console rather
  // than /app — they're not customers, and they should never see /admin/*.
  if (!isStaffUser(user) && isSalesStaff) {
    return <Navigate to="/sales" replace />;
  }

  if (!isStaffUser(user)) {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
}
